import React, { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Button, IconButton, InputAdornment, TextField, Typography } from '@mui/material';
import ArrowOutwardRoundedIcon from '@mui/icons-material/ArrowOutwardRounded';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { easyWebinarLogoLight } from '../../../../../assets/images';
import { AI_AUTH_COLORS } from '../../constants';
import { ChevronRight } from 'lucide-react';
import { toast } from 'react-toastify';
import AuthService from '../../../../../services/auth.service';

export default function TryAiFlowForgotPasswordVerifyPage() {
  const [searchParams] = useSearchParams();
  const [code, setCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState(''); 
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const email = useMemo(() => searchParams.get('email')?.trim() || '', [searchParams]);
const navigate = useNavigate();
  const isSubmitDisabled = useMemo(() => {
    if (!code.trim() || !newPassword.trim() || !confirmPassword.trim()) return true;
    return newPassword !== confirmPassword;
  }, [code, newPassword, confirmPassword]);

  const handleResendCode = useCallback(async () => {
    if (!email) return;
    try {
      await AuthService.forgotPassword(email);
      toast.success('Reset password code sent to your email');
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred. Please try again.');
    }
  }, [email]);
  const handleSubmit = useCallback(async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    try {
      await AuthService.confirmForgotPassword(email, code, newPassword);
      toast.success('Password reset successfully');
      navigate('/try-ai-flow/auth/login');
      return;
    } catch (error: any) {
      toast.error(error?.message || 'An error occurred. Please try again.');
    }
  }, [email, code, newPassword]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        bgcolor: '#ffffff',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          py: { xs: 1.5, sm: 2 },
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Link to="/try-ai-flow" style={{ display: 'inline-flex', alignItems: 'center' }}>
          <img
            src={easyWebinarLogoLight}
            alt="EasyWebinar"
            style={{ display: 'block', height: 30, width: 'auto' }}
          />
        </Link>
        <Button
          component={Link}
          to="/try-ai-flow/auth/login"
          variant="contained"
          endIcon={<ChevronRight size={16} />}
          sx={{
            textTransform: 'none',
            borderRadius: '999px',
            px: 1.9,
            py: 0.7,
            fontSize: '0.85rem',
            fontWeight: 600,
            color: '#111827',
            bgcolor: '#FFFFFF',
            boxShadow: 'none',
            '&:hover': {
              bgcolor: '#F9FAFB',
              boxShadow: 'none',
            },
          }}
        >
          Login
        </Button>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
          py: 4,
        }}
      >
        <Box sx={{ width: '100%', maxWidth: 430 }}>
          <Typography
            component="h1"
            sx={{
              textAlign: 'center',
              color: '#111827',
              fontWeight: 600,
              fontSize: { xs: '2rem', sm: '2.2rem' },
              letterSpacing: '-0.01em',
              mb: 0.8,
            }}
          >
            Enter verification code
          </Typography>
          <Typography
            sx={{
              textAlign: 'center',
              color: '#6B7280',
              fontSize: '0.92rem',
              mb: 2.2,
            }}
          >
            Enter the 6-digit code you received on your email
            {email ? (
              <>
                <br />
                <Box component="span" sx={{ color: '#111827' }}>
                  {email}
                </Box>
              </>
            ) : null}
          </Typography>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <TextField
              fullWidth
              value={code}
              onChange={(event) => setCode(event.target.value)}
              placeholder="Enter Code"
              variant="outlined"
              size="medium"
              inputProps={{ autoComplete: 'off' }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': { fontSize: '0.9rem', py: 1.1 },
                mb: 1.3,
              }}
            />

            <TextField
              fullWidth
              value={newPassword}
              onChange={(event) => setNewPassword(event.target.value)}
              placeholder="New Password"
              variant="outlined"
              size="medium"
              type={showNewPassword ? 'text' : 'password'}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowNewPassword((prev) => !prev)}
                        onMouseDown={(event) => event.preventDefault()}
                        sx={{ color: '#9CA3AF' }}
                      >
                        {showNewPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': { fontSize: '0.9rem', py: 1.1 },
                mb: 1.3,
              }}
            />

            <TextField
              fullWidth
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Confirm New Password"
              variant="outlined"
              size="medium"
              type={showConfirmPassword ? 'text' : 'password'}
              inputProps={{ autoComplete: 'new-password' }}
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                        onMouseDown={(event) => event.preventDefault()}
                        sx={{ color: '#9CA3AF' }}
                      >
                        {showConfirmPassword ? (
                          <VisibilityOffIcon fontSize="small" />
                        ) : (
                          <VisibilityIcon fontSize="small" />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  bgcolor: '#FFFFFF',
                  '& fieldset': { borderColor: '#E5E7EB' },
                  '&:hover fieldset': { borderColor: '#D1D5DB' },
                  '&.Mui-focused fieldset': { borderColor: AI_AUTH_COLORS.primaryBorder },
                },
                '& input': { fontSize: '0.9rem', py: 1.1 },
                mb: 1.6,
              }}
            />

            <Typography sx={{ textAlign: 'center', color: '#6B7280', fontSize: '0.9rem', mb: 1.8 }}>
              Didn't receive the code?{' '}
              <Typography
                component="button"
                type="button"
                sx={{
                  p: 0,
                  border: 0,
                  background: 'transparent',
                  color: AI_AUTH_COLORS.primary,
                  fontSize: 'inherit',
                  fontWeight: 600,
                  cursor: 'pointer',
                  '&:hover': { textDecoration: 'underline' },
                }}
                onClick={handleResendCode}
              >
                Resend Code
              </Typography>
            </Typography>

            <Button
              fullWidth
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled}
              sx={{
                textTransform: 'none',
                borderRadius: '10px',
                py: 1.2,
                fontSize: '0.93rem',
                fontWeight: 600,
                color: '#FFFFFF',
                bgcolor: AI_AUTH_COLORS.primaryMain,
                boxShadow: 'none',
                '&:hover': {
                  bgcolor: AI_AUTH_COLORS.primaryHover,
                  boxShadow: 'none',
                },
                '&.Mui-disabled': {
                  bgcolor: AI_AUTH_COLORS.primaryDisabled,
                  color: '#FFFFFF',
                },
              }}
            >
              Reset Password
            </Button>
          </Box>

          <Box
            sx={{
              mt: 4.6,
              textAlign: 'center',
              color: '#9CA3AF',
              fontSize: '0.86rem',
            }}
          >
            <Typography sx={{ fontSize: 'inherit', color: 'inherit', mb: 0.6 }}>
              &copy; 2026 EasyWebinar. All Rights Reserved
            </Typography>
            <Box sx={{ display: 'inline-flex', gap: 2.6 }}>
              <Typography
                component="a"
                href="https://easywebinar.com/terms-conditions/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 'inherit',
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Terms of Service
              </Typography>
              <Typography
                component="a"
                href="https://easywebinar.com/privacy/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontSize: 'inherit',
                  color: 'inherit',
                  textDecoration: 'none',
                  '&:hover': { textDecoration: 'underline' },
                }}
              >
                Privacy Policy
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
